import React, { useState } from 'react';
import { fmtINR, pct } from '../lib';
import { Section, Empty, Th, td, tr, picked } from './PlatformBits';
import StageChips, { Dash } from './StageChips';
import PlatformLogo, { PLATFORM, Platform } from './PlatformLogo';

/** The one sources table — Dashboard, Engagement, Website and Album all
 *  render their arrival sources through it. Rows are grouped under the
 *  channel they came through, the channel is derived from core's typed
 *  tuple (`source_kind` / `source` / `medium`) by rule — never from a list
 *  of campaign ids — and the long tail folds into one line per group.
 *
 *  Columns: Source · people · Tapped · New leads · Where they are, plus the
 *  caller's own summable columns (`extra`) and, when it prices sources,
 *  Spend · ₹/lead. Every number column sums up to the group line. */

/** What every sources row carries, whichever endpoint it came from. */
export interface SourceRowBase {
  tag: string | null;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  source_kind: string;
  source_label: string;
  new_leads: number;
  stages: Record<string, number>;
}

/** A caller's own numeric column; `pct` shows it as a share of people. */
export interface ExtraCol<T> { header: string; of: (r: T) => number; pct?: boolean; ours?: boolean }

/** The channel a row came through; `sub` is the one-line gloss on its heading. */
interface Group { key: string; name: string; colour: string; sub?: string; platform?: Platform }

const COLOUR = {
  search: '#A8C4A4', social: '#C9A227', direct: '#6B6B6B', whatsapp: '#355E3B',
  shares: '#8A6D1B', website: '#355E3B', other: 'rgba(61,61,61,0.25)',
};

const AD_PLATFORM: Record<string, Platform> = {
  meta: 'meta', facebook: 'meta', fb: 'meta', instagram: 'meta', ig: 'meta',
  x: 'x', twitter: 'x', google: 'google', youtube: 'google', linkedin: 'linkedin', openai: 'chatgpt', chatgpt: 'chatgpt',
};

/** A referrer host that is a search engine's own result page. */
const SEARCH_HOST = /^(www\.)?(google\.[a-z.]+|bing\.com|yahoo\.com|duckduckgo\.com|yandex\.[a-z]+)$|googlequicksearchbox/;
/** A referrer host that is a social network (organic, not our ads). */
const SOCIAL_HOST = /facebook|instagram|twitter|(^|\.)x\.com$|(^|\.)t\.co$|linkedin|youtube|whatsapp|threads\.net|pinterest|reddit/;

const GROUPS: Record<string, Group> = {
  search: { key: 'search', name: 'Organic search', colour: COLOUR.search },
  social: { key: 'social', name: 'Social', colour: COLOUR.social, sub: 'organic, from a post or a profile' },
  direct: { key: 'direct', name: 'Direct', colour: COLOUR.direct, sub: 'no tag, no referrer' },
  whatsapp: { key: 'whatsapp', name: 'Our WhatsApp sends', colour: COLOUR.whatsapp },
  shares: { key: 'shares', name: 'Shares', colour: COLOUR.shares, sub: 'a visitor forwarded a link' },
  website: { key: 'website', name: 'Website buttons', colour: COLOUR.website, sub: 'came to WhatsApp from the site' },
  other: { key: 'other', name: 'Other', colour: COLOUR.other },
};
const adsGroup = (platform: Platform): Group =>
  ({ key: `ads:${platform}`, name: `${PLATFORM[platform].label} ads`, colour: PLATFORM[platform].colour, platform });

export const groupOf = (r: SourceRowBase): Group => {
  const src = (r.source ?? '').toLowerCase();
  const med = (r.medium ?? '').toLowerCase();
  switch (r.source_kind) {
    case 'ad': return adsGroup('meta');
    case 'campaign': case 'welcome': case 'manual': return GROUPS.whatsapp;
    case 'share': return GROUPS.shares;
    case 'web': return GROUPS.website;
    case 'utm': {
      if (med === 'referral' || src.includes('.')) {
        if (SEARCH_HOST.test(src)) return GROUPS.search;
        if (SOCIAL_HOST.test(src)) return GROUPS.social;
        return GROUPS.other;
      }
      const platform = AD_PLATFORM[src];
      return platform ? adsGroup(platform) : GROUPS.other;
    }
    default: return r.tag == null ? GROUPS.direct : GROUPS.other;
  }
};

/** The row's name under its group heading: core's label minus the words the
 *  heading already says. A referrer is named by its host; a share by its
 *  code; an ad by its id (the Dashboard swaps in the campaign name). */
const GENERIC_MEDIUM = new Set(['paid', 'broadcast', 'share', 'cta']);
export const rowName = (r: SourceRowBase): string => {
  if (r.source_kind === 'welcome') return 'welcome message';
  if (r.source_kind === 'manual') return 'operator send';
  if (r.medium === 'referral') return r.source ?? r.source_label;
  const parts = r.source_label.split(' · ');
  const rest = parts.filter((p, i) => !(i === 0 && p === r.source) && !(p === r.medium && GENERIC_MEDIUM.has(p)));
  const name = rest.length ? rest.join(' · ') : r.source_label;
  return r.source_kind === 'ad' ? `ad ${name}` : name;
};

export const rowKey = (r: SourceRowBase) => r.tag ?? 'direct';

/** Everything a line shows, summable: a row, a group, or a fold of small rows. */
interface Sums { people: number; tapped: number; new_leads: number; stages: Record<string, number>; extra: number[]; spend: number | null }

const FOLD_SHARE = 0.01;

export function SourcesList<T extends SourceRowBase>({
  title, note, rows, people, peopleHeader, tapped, extra = [], label, spendOf, groupSpend, selected = null, onSelect, collapsible = false,
}: {
  title: string;
  note?: React.ReactNode;
  rows: T[];
  /** The denominator — people, visitors or opens — and its column header. */
  people: (r: T) => number;
  peopleHeader: string;
  tapped: (r: T) => number;
  /** Caller-specific summable columns, placed after the denominator. */
  extra?: ExtraCol<T>[];
  /** Override the row name (undefined falls back to the rule). */
  label?: (r: T) => string | undefined;
  spendOf?: (r: T) => number | null;
  /** Spend known only for a whole group (key as `groupOf` returns it, e.g. `ads:x`). */
  groupSpend?: (groupKey: string) => number | null;
  /** `rowKey` of the highlighted row; clicking it again selects null. */
  selected?: string | null;
  onSelect?: (row: T | null) => void;
  /** Multi-source groups start folded to their heading line (the platform's
   *  total); the heading's chevron opens the detail. */
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const flip = (set: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) =>
    set((o) => { const n = new Set(o); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const sum = (rs: T[]): Sums => rs.reduce<Sums>((a, r) => {
    const sp = spendOf ? spendOf(r) : null;
    Object.entries(r.stages).forEach(([st, c]) => { a.stages[st] = (a.stages[st] ?? 0) + c; });
    return {
      people: a.people + people(r), tapped: a.tapped + tapped(r), new_leads: a.new_leads + r.new_leads, stages: a.stages,
      extra: extra.map((e, i) => a.extra[i] + e.of(r)),
      spend: sp == null ? a.spend : (a.spend ?? 0) + sp,
    };
  }, { people: 0, tapped: 0, new_leads: 0, stages: {}, extra: extra.map(() => 0), spend: null });
  const richer = (a: Sums, b: Sums) => b.new_leads - a.new_leads || b.people - a.people;

  const total = rows.reduce((a, r) => a + people(r), 0);
  const small = (r: T) => people(r) < Math.max(2, total * FOLD_SHARE) && r.new_leads === 0;
  const groups = new Map<string, { group: Group; rows: T[] }>();
  rows.forEach((r) => {
    const g = groupOf(r);
    if (!groups.has(g.key)) groups.set(g.key, { group: g, rows: [] });
    groups.get(g.key)!.rows.push(r);
  });
  // A group's spend is the platform's own total when the caller knows it as
  // one number (X, Google), else the sum of its rows (Meta, per campaign).
  const ordered = [...groups.values()]
    .map((g) => {
      const sums = sum(g.rows);
      const gs = groupSpend?.(g.group.key);
      return { ...g, sums: gs == null ? sums : { ...sums, spend: gs }, rows: [...g.rows].sort((a, b) => richer(sum([a]), sum([b]))) };
    })
    .sort((a, b) => (a.group.key === 'other' ? 1 : 0) - (b.group.key === 'other' ? 1 : 0) || richer(a.sums, b.sums));

  const cols = 5 + extra.length + (spendOf ? 2 : 0);
  const nums = (s: Sums, strong?: boolean, muted?: boolean) => {
    const tone = muted ? 'text-graphite' : '';
    return (
      <>
        <td className={`${td} text-right ${strong ? 'font-semibold text-[14.5px]' : ''} ${tone}`}>{s.people ? s.people.toLocaleString('en-IN') : <Dash />}</td>
        {extra.map((e, i) => (
          <td key={e.header} className={`${td} text-right ${tone}`}>
            {s.extra[i] ? <>{s.extra[i].toLocaleString('en-IN')}{e.pct && s.people > 0 && <Pct>{pct(s.extra[i], s.people)}</Pct>}</> : <Dash />}
          </td>
        ))}
        <td className={`${td} text-right ${tone}`}>{s.tapped ? <>{s.tapped.toLocaleString('en-IN')}{s.people > 0 && <Pct>{pct(s.tapped, s.people)}</Pct>}</> : <Dash />}</td>
        <td className={`${td} text-right text-forest font-semibold ${strong ? 'text-[14.5px]' : ''}`}>{s.new_leads || <Dash />}</td>
        <td className="px-3 py-2.5 text-right max-w-[260px]">{s.new_leads ? <StageChips stages={s.stages} /> : <Dash />}</td>
        {spendOf && <td className={`${td} text-right ${tone}`}>{s.spend != null ? fmtINR(s.spend) : <Dash />}</td>}
        {spendOf && <td className={`${td} text-right ${tone}`}>{s.spend != null && s.new_leads ? fmtINR(s.spend / s.new_leads) : <Dash />}</td>}
      </>
    );
  };

  /** A selectable source line. As a group's only row it wears the group's
   *  dot and name, with the row's own name as the sub-text. */
  const line = (r: T, alone?: Group) => {
    const key = rowKey(r);
    const isSel = selected != null && selected === key;
    const name = label?.(r) ?? rowName(r);
    return (
      <tr key={key} onClick={() => onSelect?.(isSel ? null : r)} aria-selected={isSel}
          className={`${rowCls(!!alone)} ${onSelect ? 'cursor-pointer transition-colors' : ''} ${
            isSel ? `bg-sage/25 ${picked(true)}` : onSelect ? 'hover:bg-charcoal/[0.03]' : ''}`}>
        <td className={`px-3 py-3 ${alone ? '' : 'pl-9'}`} title={r.tag ?? 'no tag, no referrer'}>
          {alone && <Dot g={alone} />}
          <span className={`${alone ? 'font-semibold text-[13.5px] text-charcoal' : 'text-[13px]'} ${isSel ? 'text-forest font-semibold' : ''}`}>{alone ? alone.name : name}</span>
          {alone && <Sub>{alone.sub ?? (name !== alone.name ? name : null)}</Sub>}
          {isSel && <span className="ml-2 text-[11px] font-semibold text-forest">selected · click to clear</span>}
        </td>
        {nums(sum([r]), !!alone)}
      </tr>
    );
  };

  return (
    <Section title={title} note={note}>
      <table className="w-full min-w-[720px] border-collapse">
        <thead><tr>
          <Th left>Source</Th><Th>{peopleHeader}</Th>
          {extra.map((e) => <Th key={e.header} ours={e.ours}>{e.header}</Th>)}
          <Th>Tapped</Th><Th ours>New leads</Th><Th ours>Where they are</Th>
          {spendOf && <><Th>Spend</Th><Th>₹ / lead</Th></>}
        </tr></thead>
        <tbody>
          {rows.length === 0 && <Empty cols={cols}>No arrivals in this window</Empty>}
          {ordered.map(({ group, rows: gRows, sums }) => {
            if (gRows.length === 1) return line(gRows[0], group);
            if (group.key === 'shares') {
              // Share tokens name nobody; one line for all of them.
              return (
                <tr key={group.key} className={rowCls(true)}>
                  <td className="px-3 py-3"><Dot g={group} /><span className="font-semibold text-[13.5px]">{group.name}</span><Sub>{gRows.length} forwarded links</Sub></td>
                  {nums(sums, true)}
                </tr>
              );
            }
            const folded = gRows.filter(small);
            const fold = folded.length >= 2;
            const visible = fold ? gRows.filter((r) => !small(r)) : gRows;
            const isOpen = open.has(group.key);
            const toggle = () => flip(setOpen, group.key);
            const showHide = <span className="text-forest font-semibold ml-1.5">{isOpen ? 'hide ▴' : 'show ▾'}</span>;
            // A group that is all small rows is its own fold: the heading toggles it.
            const allSmall = fold && visible.length === 0;
            // Folded groups show only their heading; the one holding the
            // selected source stays open so a selection is never hidden.
            const holdsSelected = selected != null && gRows.some((r) => rowKey(r) === selected);
            const shut = collapsible && !allSmall && !expanded.has(group.key) && !holdsSelected;
            const headClick = collapsible && !allSmall ? () => flip(setExpanded, group.key) : allSmall ? toggle : undefined;
            return (
              <React.Fragment key={group.key}>
                <tr onClick={headClick} aria-expanded={collapsible && !allSmall ? !shut : undefined}
                    className={`${rowCls(true)} ${headClick ? 'cursor-pointer hover:bg-charcoal/[0.03]' : ''}`}>
                  <td className="px-3 py-3">
                    {collapsible && !allSmall && (
                      <span className={`inline-block w-3 mr-1.5 text-graphite text-[10px] transition-transform ${shut ? '' : 'rotate-90'}`}>▶</span>
                    )}
                    <Dot g={group} /><span className="font-semibold text-[13.5px]">{group.name}</span>
                    <Sub>{allSmall ? <>{gRows.length} small sources{showHide}</> : shut ? `${gRows.length} sources` : group.sub}</Sub>
                  </td>
                  {nums(sums, true)}
                </tr>
                {shut ? null : <>
                {visible.map((r) => line(r))}
                {fold && !allSmall && (
                  <tr onClick={toggle} className={`${rowCls(false)} cursor-pointer hover:bg-charcoal/[0.03]`}>
                    <td className="px-3 py-3 pl-9 text-graphite text-[12.5px]">{folded.length} smaller sources{showHide}</td>
                    {nums(sum(folded), false, true)}
                  </tr>
                )}
                {fold && isOpen && folded.map((r) => line(r))}
                </>}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Section>
  );
}

/** A group's heading line (or a lone-row group) opens with a firmer hairline
 *  so each channel reads as its own block; its member rows hang under it on
 *  the kit's faint rule. */
const rowCls = (head: boolean) => `${tr} ${head ? 'border-t border-t-charcoal/15 first:border-t-0' : ''}`;

const Pct: React.FC<{ children: React.ReactNode }> = ({ children }) => <span className="text-graphite text-[11px] ml-1.5">{children}</span>;
const Dot: React.FC<{ g: Group }> = ({ g }) => g.platform
  ? <PlatformLogo platform={g.platform} className="inline-block w-3.5 h-3.5 mr-2 align-[-2px]" />
  : <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ backgroundColor: g.colour }} />;
const Sub: React.FC<{ children?: React.ReactNode }> = ({ children }) => (children ? <span className="text-graphite text-[11.5px] ml-2">{children}</span> : null);

export default SourcesList;
